import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { useResize } from "../../utils/useResize";
import moment from "moment";
import { INITIATIVES_URL } from "../../api/urls";
import { GetHook } from "../../api/api";
import { AddInitiative } from "./AddInitiative";
import { get } from "../../utils/get";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import DetailsView from "./DetailsView";
import Modal from "../../ui/Modal";
import { ReloadPlanSubject } from "../../subjects/ReloadPlanSubject";
import { Clock } from "../../assets/svg/Clock";
import CheckIcon from "../../assets/svg/CheckIcon";
import { colors } from "../../utils/colors";
type DummyData = {
  id: number;
  start: Date;
  end: Date;
  position_id: number;
  color: string;
  name: string;
  status: string;
}[];
interface IProps {
  initiativeFilter?: string;
  objectiveFilter?: string;
  dateFilter?: string;
  ownersFilter?: string;
  createdByFilter?: string;
}

export const Initiatives = ({
  initiativeFilter,
  objectiveFilter,
  dateFilter,
  ownersFilter,
  createdByFilter,
}: IProps) => {
  const date =
    get(dateFilter, "startDate") && get(dateFilter, "endDate")
      ? `interval=${moment(get(dateFilter, "startDate")).format(
          "YYYY-MM-DD"
        )}/${moment(get(dateFilter, "endDate")).format("YYYY-MM-DD")}`
      : "";

  const location = useLocation();
  const idToReload = get(location, "key");
  const { data, reload } = GetHook(
    objectiveFilter && ownersFilter && createdByFilter
      ? `${INITIATIVES_URL}?owners=${ownersFilter}&objective_id=${objectiveFilter}&created_by=${createdByFilter}&${date}`
      : objectiveFilter && createdByFilter
      ? `${INITIATIVES_URL}?objective_id=${objectiveFilter}&created_by=${createdByFilter}&${date}`
      : objectiveFilter && ownersFilter
      ? `${INITIATIVES_URL}?objective_id=${objectiveFilter}&owners=${ownersFilter}&${date}`
      : date && ownersFilter && createdByFilter
      ? `${INITIATIVES_URL}?owners=${ownersFilter}&created_by=${createdByFilter}&${date}`
      : objectiveFilter
      ? `${INITIATIVES_URL}?objective_id=${objectiveFilter}&${date}`
      : ownersFilter
      ? `${INITIATIVES_URL}?owners=${ownersFilter}&${date}`
      : createdByFilter
      ? `${INITIATIVES_URL}?created_by=${createdByFilter}&${date}`
      : date
      ? `${INITIATIVES_URL}?${date}`
      : "null"
  );

  useEffect(() => {
    const planSub = ReloadPlanSubject.subscribe((payload) => {
      if (payload) {
        reload(idToReload);
        ReloadPlanSubject.next(false);
      }
    });
    return () => {
      planSub.unsubscribe();
    };
  }, [idToReload, reload]);

  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [isModalOpen, setisModalOpen] = useState(false);
  const [editData, setEditData] = useState([]);
  const filteredData = data.filter(
    (x) =>
      Array.isArray(get(x, "periods", [])) && get(x, "periods", []).length > 0
  );
  const timelineData: DummyData = filteredData
    .map((timeline, index) =>
      timeline?.periods.map((period, i) => {
        return {
          id: timeline?.id,
          position_id: timeline?.id + (i + 1),
          name: timeline?.name,
          start: new Date(period?.start_date),
          end: new Date(period?.end_date),
          color: colors[index],
          status: timeline?.status,
        };
      })
    )
    .flat();

  const componentRef = useRef(null);
  let { width, height } = useResize(componentRef);
  width = width - 10;
  let chartHeight = height * 0.8 * (timelineData.length / 12);
  const minX = d3.min(timelineData, (d) => d.start) || new Date();
  const maxX = d3.max(timelineData, (d) => d.end) || new Date();
  const allIds = timelineData.map((filteredData) => filteredData.position_id);
  const uniqueIds = allIds
    .filter((v, i, a) => a.indexOf(v) === i)
    .map((item) => `${item}`);

  const scaleX = d3.scaleTime().domain([minX, maxX]).range([0, width]).nice();

  const scaleY = d3
    .scalePoint()
    .domain(uniqueIds)
    .range([0, chartHeight])
    .padding(0.5);

  const xAxis = scaleX.ticks();

  return (
    <>
      {isModalOpen && (
        <Modal
          title="Add Task"
          noFooter
          noHeader
          closeModal={() => setisModalOpen(false)}
        >
          <AddInitiative
            onClick={() => setisModalOpen(false)}
            isEdit
            isOpen={isModalOpen}
            editData={editData}
            reloadData={() => reload(idToReload)}
          />
        </Modal>
      )}
      {isPaneOpen && (
        <DetailsView
          onClick={() => setIsPaneOpen(false)}
          setisModalOpen={setisModalOpen}
          isOpen={isPaneOpen}
          reloadData={() => reload(idToReload)}
          data={editData}
        />
      )}
      <InitiativesContainer ref={componentRef}>
        <svg
          width={width}
          height={height * 1 * (timelineData.length / 10)}
          overflow="auto"
          preserveAspectRatio="xMidYMid meet"
        >
          <text x={20} y={20} fontWeight={600}>
            Timeline
          </text>
          <svg>
            {xAxis.map((axis) => {
              return (
                <>
                  <line
                    x1={scaleX(axis) + 20}
                    x2={scaleX(axis) + 20}
                    y1={0}
                    y2={height * 1 * (timelineData.length / 10)}
                    stroke="#3751FF"
                    strokeOpacity={0.1}
                    strokeWidth={1}
                    strokeDasharray="5 5"
                  />
                  <text x={scaleX(axis) + 20} y={40}>
                    {moment(axis).format("MMM")}
                  </text>
                </>
              );
            })}
          </svg>
          40
          <svg>
            {xAxis.map((axis) => {
              return (
                <>
                  <text
                    x={scaleX(axis) + 20}
                    y={height * 1 * (timelineData.length / 10) - 50}
                  >
                    {moment(axis).format("MMM")}
                  </text>
                </>
              );
            })}
          </svg>
          <>
            <svg overflow="auto" width={width} height="1122px">
              {timelineData.map((tdata, index) => (
                <>
                  <g
                    onClick={() => {
                      const selectedInitiative = filteredData.filter(
                        (x) => get(x, "id") === tdata.id
                      );
                      setEditData(selectedInitiative);
                      setIsPaneOpen(true);
                    }}
                  >
                    <rect
                      height={35}
                      x={scaleX(tdata.start) + 20}
                      y={
                        (scaleY(`${tdata.position_id}`) as number) +
                        chartHeight * 0.1
                      }
                      fill={tdata.color}
                      width={10}
                      rx={5}
                    />
                    <rect
                      width={scaleX(tdata.end) - scaleX(tdata.start)}
                      height={35}
                      x={scaleX(tdata.start) + 20}
                      y={
                        (scaleY(`${tdata.position_id}`) as number) +
                        chartHeight * 0.1
                      }
                      fill={tdata.color}
                      opacity={0.2}
                      rx={5}
                    />
                    <foreignObject
                      x={scaleX(tdata.start) + 15 + 20}
                      y={
                        (scaleY(`${tdata.position_id}`) as number) +
                        chartHeight * 0.1
                      }
                      fill={tdata.color}
                      width={scaleX(tdata.end) - scaleX(tdata.start) - 15}
                      height={35}
                    >
                      <p
                        style={{
                          fontSize: 12,
                          color: `${tdata.color}`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {tdata.name}
                      </p>
                    </foreignObject>
                    {tdata.status === "complete" && (
                      <foreignObject
                        x={scaleX(tdata.start) - 5}
                        y={
                          (scaleY(`${tdata.position_id}`) as number) +
                          chartHeight * 0.1
                        }
                        fill={tdata.color}
                        width={scaleX(tdata.end) - scaleX(tdata.start) - 15}
                        height={35}
                      >
                        <p
                          style={{
                            fontSize: 12,
                            color: `${tdata.color}`,
                            overflow: "hidden",
                            wordWrap: "normal",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <CheckIcon fill={tdata.color} />
                        </p>
                      </foreignObject>
                    )}
                  </g>
                </>
              ))}
            </svg>
          </>
        </svg>
      </InitiativesContainer>
    </>
  );
};

const InitiativesContainer = styled.div`
  background-color: #fff;
  height: 43rem;
  overflow: scroll;
`;
